import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import {
  API_SUBSCRIPTION_CARD_CANCEL,
  API_SUBSCRIPTION_SMS_CANCEL,
} from '@/api/endpoints';
import { USER_QUERY_KEY } from '@/api/queryKeys';
import { SubscriptionPaymentKind } from '@/types/user';
import { ErrorMessage, logRequestError } from '@/utils/errors';

const cancelEndpoints: Record<SubscriptionPaymentKind, string> = {
  [SubscriptionPaymentKind.Sms]: API_SUBSCRIPTION_SMS_CANCEL,
  [SubscriptionPaymentKind.Card]: API_SUBSCRIPTION_CARD_CANCEL,
};

const cancelErrorMessages: Record<SubscriptionPaymentKind, ErrorMessage> = {
  [SubscriptionPaymentKind.Sms]: ErrorMessage.CancelSmsSubscription,
  [SubscriptionPaymentKind.Card]: ErrorMessage.CancelCardSubscription,
};

interface Params {
  onSuccess: (kind: SubscriptionPaymentKind) => void;
  onError: (kind: SubscriptionPaymentKind) => void;
}

export const useCancelSubscription = ({ onSuccess, onError }: Params) => {
  const queryClient = useQueryClient();

  const { mutate: cancelSubscription, isPending } = useMutation({
    mutationFn: (kind: SubscriptionPaymentKind) =>
      apiClient.post(cancelEndpoints[kind]),
    onSuccess: async (_response, kind) => {
      await queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
      onSuccess(kind);
    },
    onError: (error, kind) => {
      logRequestError(cancelErrorMessages[kind], error);
      onError(kind);
    },
  });

  return { cancelSubscription, isPending };
};
